import React, { memo } from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { getWeekDates } from "../../utils/calendarHelpers"
import styles from "../../EquipmentCalendar.module.css"
import { Boat } from "ui/Icon/DSIcon/Boat"
import { spacings } from "themes/variables"
import { Text } from "ui/Typography"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { DSIcon } from "ui/Icon/DSIcon"
import { getJournalIcon } from "pagesV2/pro/Calendar/utils"
import IconWithBg from "ui/IconWithBg"

/**
 * Week view calendar component
 * Displays a weekly calendar grid with assets and bookings
 */
const WeekView = ({ selectedDate, assets, onBookingClick, journalNoteDates, onJournalClick, canCreateJournal = true, canEditJournal = true, canViewJournal = true }) => {
  const { t } = useTranslation()
  const weekDates = getWeekDates(selectedDate)

  return (
    <div className={styles.weekGrid}>
      {/* Header Row with Days */}
      <div className={styles.weekGridHeader}>
        <Flex vertical gap={spacings.XXS} className={styles.assetColumnHeader}>
          <Boat />
          <Text fw={500} type="secondary">
            {t("equipment.calendar.assets")}
          </Text>
        </Flex>
        {weekDates.map((date) => {
          const dayName = date.toLocaleDateString("en-US", { weekday: "short" })
          const dayNum = date.getDate()
          const dateString = moment(date).format("YYYY-MM-DD")
          return (
            <div key={date.toISOString()} className={styles.dayColumnHeader}>
              <Flex justify="space-between" align="center" style={{ width: "100%" }}>
                <Flex vertical gap={spacings.XXS}>
                  <Text type="secondary">{dayName}</Text>
                  <Text strong className={styles.dayNumber}>
                    {dayNum}
                  </Text>
                </Flex>
                {(() => {
                  const hasJournal = journalNoteDates?.includes(dateString)
                  const canShowIcon = (hasJournal && (canEditJournal || canViewJournal)) || (!hasJournal && canCreateJournal)
                  return canShowIcon ? (
                    <IconWithBg
                      icon={getJournalIcon(journalNoteDates || [], dateString)}
                      onClick={() => onJournalClick(dateString)}
                      style={{ cursor: "pointer" }}
                    />
                  ) : null
                })()}
              </Flex>
            </div>
          )
        })}
      </div>

      {/* Asset Rows */}
      <WithEmptyStateWrapper
        name="calendar-assets"
        hasItems={assets?.length > 0}
        fallbackTitle={t("equipment.calendar.no_assets", { defaultValue: "No assets found" })}
        fallbackDescription={t("equipment.calendar.no_assets_description", {
          defaultValue: "Try adjusting your filters or add new assets"
        })}
        fallbackImageSrc={<DSIcon name="boat" width={80} height={80} />}
      >
        <div className={styles.weekGridBody}>
          {assets.map((asset) => (
            <div key={asset.id} className={styles.assetRow}>
              {/* Asset Name Column */}
              <div className={styles.assetNameCell}>
                <Text>
                  {t("equipment.calendar.asset")} #{asset.number}
                </Text>
              </div>

              {/* Day Columns */}
              {weekDates.map((date) => (
                <div key={`${asset.id}-${date.toISOString()}`} className={styles.dayCell}>
                  {/* Booking blocks for this asset on this day */}
                  {asset.bookings
                    ?.filter((booking) => {
                      const bookingDate = new Date(booking.date)
                      return (
                        bookingDate.getDate() === date.getDate() &&
                        bookingDate.getMonth() === date.getMonth() &&
                        bookingDate.getFullYear() === date.getFullYear()
                      )
                    })
                    .map((booking) => (
                      <button
                        key={booking.id}
                        className={`${styles.weekBookingBlock} ${
                          booking.paymentStatus === "full_paid" ? styles.paidBooking : styles.outstandingBooking
                        }`}
                        aria-label={`Open booking ${booking.tripName} at ${booking.timeRange}`}
                        onClick={() => onBookingClick(booking.uuid)}
                        type="button"
                      >
                        <Text strong className={styles.bookingTime}>
                          {booking.timeRange}
                        </Text>
                        <Text className={`${styles.bookingTrip} ${styles.ellipsisText} ellipsis`}>{booking.tripName}</Text>
                      </button>
                    ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </WithEmptyStateWrapper>
    </div>
  )
}

WeekView.propTypes = {
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  assets: PropTypes.array.isRequired,
  onBookingClick: PropTypes.func.isRequired,
  journalNoteDates: PropTypes.array,
  onJournalClick: PropTypes.func.isRequired,
  canCreateJournal: PropTypes.bool,
  canEditJournal: PropTypes.bool,
  canViewJournal: PropTypes.bool
}

export default memo(WeekView)
